import { useCallback, useMemo, useRef } from 'react'
import { sendLogs } from './helpers'

import { LogLevel, TOOLTIP_ACTION, TOOLTIP_TIME_LIMIT } from './constants'

const PROPERTIES_LIMIT = 200
const MILLISECONDS = 1000

export const METRICS_GROUP = 'metrics'
export const FLOW_GROUP = 'flow'

const getDate = () => {
    return Date.now()
}

const addTimeSpent = (params, timeSpent) => {
    if (params === undefined) {
        params = {}
    }
    params['timeSpent'] = timeSpent
    return params
}

const convertMetricsParams = ({ partnerID, item, parentScreen, itemState, info }) => {
    return {
        item,
        partnerID: partnerID ? partnerID : null,
        parent_screen: parentScreen ? parentScreen : null,
        item_state: itemState ? itemState : null,
        additional_info: info ? info : null,
    }
}

const convertFlowParams = ({ sourceItem, destinationItem, transitionMethod, partnerID }) => {
    return {
        partnerID: partnerID ? partnerID : null,
        source_item: sourceItem ? sourceItem : null,
        destination_item: destinationItem ? destinationItem : null,
        transition_method: transitionMethod ? transitionMethod : null,
    }
}

/*
    NOTE!
    DO NOT USE base hooks directly (_useLog, ...).
    Use Metrics or Flow hooks instead.
*/
const _useLog = (feature, group) => {
    const log = useCallback(
        (action, logLevel = LogLevel.Info, params) => {
            if (!params) {
                params = {}
            }
            if (Object.keys(params).length >= PROPERTIES_LIMIT) {
                return
            }

            sendLogs(feature, group, action, logLevel, params)
        },
    [feature, group],
    )

    return (action, logLevel, params) => log(action, logLevel, params)
}

const _useLogOnce = (feature, group) => {
    const logger = _useLog(feature, group)
    const actions = useRef([])

    const logOnce = useCallback(
    (action, logLevel, params) => {
        if (!action || actions.current.includes(action)) {
            return
        } else {
            actions.current.push(action)
        }

        logger(action, logLevel, params)
    },[actions, logger])

    const reset = () => {
        actions.current = []
    }

    return [(action, logLevel, params) => logOnce(action, logLevel, params), () => reset()]
}

const _useLogTimeSpent = (feature, group) => {
    const logger = _useLog(feature, group)
    const actions = useRef(new Map())
    const suspendedActions = useRef(new Map())

    const start = useCallback(
        (action) => {
            if (!action) {
                return
            }
            const key = actions.current.get(action)
            if (key !== undefined && key > 0) {
                return
            } else {
                actions.current.set(action, getDate())
            }
        },
        [actions],
    )

    const reset = useCallback(() => {
        actions.current.clear()
        suspendedActions.current.clear()
    }, [actions, suspendedActions])

    const suspend = useCallback(
        (action) => {
            if (!action) {
                return
            }
            if (actions.current.get(action) === undefined || suspendedActions.current.get(action) !== undefined) {
                return
            }

            suspendedActions.current.set(action, getDate())
        },
        [actions, suspendedActions],
    )

    const resume = useCallback(
        (action) => {
            if (!action) {
                return
            }
            const prevActionTime = actions.current.get(action)
            if (prevActionTime === undefined) {
                return
            }

            const prevSusTime = suspendedActions.current.get(action)
            if (prevSusTime === undefined) {
                return
            }

            suspendedActions.current.delete(action)

            const suspensionTime = getDate() - prevSusTime
            actions.current.set(action, prevActionTime + suspensionTime)
        },
        [actions, suspendedActions],
    )

    const stop = useCallback(
        (action, timeLimit = 0, logLevel, params) => {
            const prevDate = actions.current.get(action)
            if (prevDate === undefined) {
                return
            }

            const prevSusTime = suspendedActions.current.get(action)
            if (prevSusTime !== undefined) {
                resume(action)
            }

            actions.current.delete(action)

            const timeSpent = (getDate() - prevDate) / MILLISECONDS
            if (timeSpent <= timeLimit) {
                return
            }
            params = addTimeSpent(params, timeSpent)

            logger(action, logLevel, params)
        },
    [actions, suspendedActions, logger, resume],
)

    return [
        (action) => start(action),
        (action, timeLimit, logLevel, params) =>
    stop(action, timeLimit, logLevel, params),
        () => reset(),
        (action) => suspend(action),
        (action) => resume(action),
    ]
}

/*
    Use Metrics logger to log some user action(button click, etc).
    More info about log args: https://confluence.wargaming.net/x/dPWyZw

    const log = useMetricsLog(feature);
*/
const useMetricsLog = (feature) => {
    const logger = _useLog(feature, METRICS_GROUP)

    const log = useCallback(
        (params) => {
            logger(params.action, params.logLevel, convertMetricsParams(params))
        },
        [logger],
    )
    return (params) => log(params)
}

/*
    const [log, reset] = useMetricsLogOnce(feature);
*/
const useMetricsLogOnce = (feature) => {
    const [logOnce, reset] = _useLogOnce(feature, METRICS_GROUP)

    const logMetricsOnce = useCallback(
        (params) => {
            const { action, logLevel } = params
            logOnce(action, logLevel, convertMetricsParams(params))
        },
        [logOnce],
    )

    return [(params) => logMetricsOnce(params), () => reset()]
}

/*
    const [start, stop, reset, suspend, resume] = useMetricsLogTimeSpent(feature);
*/
const useMetricsLogTimeSpent = (feature) => {
    const [start, stop, reset, suspend, resume] = _useLogTimeSpent(feature, METRICS_GROUP)

    const metricsStop = useCallback(
        (params) => {
            const { action, timeLimit, logLevel } = params
            stop(action, timeLimit, logLevel, convertMetricsParams(params))
        },
        [stop],
    )

    return [
        (action) => start(action),
        (params) => metricsStop(params),
        () => reset(),
        (action) => suspend(action),
        (action) => resume(action),
    ]
}

/*
    Use Flow logger to log transactions between views.
    More info about log args: https://confluence.wargaming.net/x/dPWyZw

    const log = useFlowLog(feature);
*/
const useFlowLog = (feature) => {
    const logger = _useLog(feature, FLOW_GROUP)

    const log = useCallback(
        (params) => {
            logger(params.action, params.logLevel, convertFlowParams(params))
        },
        [logger],
    )

    return (params) => log(params)
}

/*
    const [log, reset] = useFlowLogOnce(feature);
*/
const useFlowLogOnce = (feature) => {
    const [logOnce, reset] = _useLogOnce(feature, FLOW_GROUP)

    const logMetricsOnce = useCallback(
        (params) => {
            const { action, logLevel } = params
            logOnce(action, logLevel, convertFlowParams(params))
        },
        [logOnce],
    )

    return [(params) => logMetricsOnce(params), () => reset()]
}

/*
    const [start, stop, reset, suspend, resume] = useFlowLogTimeSpent(feature);
*/
const useFlowLogTimeSpent = (feature) => {
    const [start, stop, reset, suspend, resume] = _useLogTimeSpent(feature, FLOW_GROUP)

    const flowStop = useCallback(
        (params) => {
            const { action, timeLimit, logLevel } = params
            stop(action, timeLimit, logLevel, convertFlowParams(params))
        },
        [stop],
    )

    return [
        (action) => start(action),
        (params) => flowStop(params),
        () => reset(),
        (action) => suspend(action),
        (action) => resume(action),
    ]
}

/*
    Used to log 'tooltip watched' action
        const checkBoxTooltipLogger = useTooltipLogger(FEATURE, {
            item: Items.AbilitiesCheckboxTooltip,
            parentScreen: ParentScreen.SetupView,
        });
        ...
        <SimpleTooltip
            {...checkBoxTooltipLogger}
        >
*/
export const useTooltipLogger = (feature, params) => {
    const [start, stop] = useMetricsLogTimeSpent(feature)
    const { action, timeLimit, ...tooltipParams } = params
    return useMemo(() => {
        return {
            onShow: () => start(action ? action : TOOLTIP_ACTION),
            onHide: () =>
                stop({
                    action: action ? action : TOOLTIP_ACTION,
                    timeLimit: timeLimit ? timeLimit : TOOLTIP_TIME_LIMIT,
                    ...tooltipParams,
                }),
        }
    }, [action, timeLimit, tooltipParams, start, stop])
}

export {
    useMetricsLog,
    useMetricsLogOnce,
    useMetricsLogTimeSpent,
    useFlowLog,
    useFlowLogOnce,
    useFlowLogTimeSpent,
}



// WEBPACK FOOTER //
// ./helpers/uilogging/hooks.js